import React from 'react';
import { usePermissions } from '../../contexts/PermissionContext';

/**
 * PermissionGate - Conditionally renders content based on user permissions
 * 
 * @param {string|string[]} permission - Permission code(s) to check
 * @param {React.ReactNode} children - Content to render if permission is granted
 * @param {React.ReactNode} fallback - Content to render if permission is denied (default: null)
 * @param {boolean} requireAll - If multiple permissions, require all (default: false - require any)
 * @param {boolean} showFallback - Whether to show fallback content (default: true)
 */
const PermissionGate = ({ 
  permission, 
  children, 
  fallback = null, 
  requireAll = false,
  showFallback = true 
}) => {
  const { hasPermission, hasPermissions, isLoaded } = usePermissions();

  // Show loading state while permissions are being fetched
  if (!isLoaded) {
    return null; // Or a loading spinner if desired
  }

  // Handle single permission
  if (typeof permission === 'string') {
    const allowed = hasPermission(permission);
    return allowed ? children : (showFallback ? fallback : null);
  }

  // Handle multiple permissions
  if (Array.isArray(permission)) {
    const permissionResults = hasPermissions(permission);
    const permissionValues = Object.values(permissionResults);
    
    const allowed = requireAll 
      ? permissionValues.every(Boolean)  // All permissions required
      : permissionValues.some(Boolean);  // Any permission sufficient
    
    return allowed ? children : (showFallback ? fallback : null);
  }

  // Invalid permission format
  console.warn('PermissionGate: permission must be a string or array of strings');
  return showFallback ? fallback : null;
};

/**
 * PermissionGuard - Alternative component for protecting entire routes or sections
 * Shows a professional "access denied" message instead of hiding content
 */
export const PermissionGuard = ({ 
  permission, 
  children, 
  message = "You don't have permission to access this feature.",
  requireAll = false 
}) => {
  const fallback = (
    <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg border border-gray-200">
      <div className="text-center">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-4V7a3 3 0 00-6 0v4m2-4h3m-3 0h3m-3 0V7a3 3 0 013 3v4" />
          </svg>
        </div>
        <p className="text-gray-600 font-medium">{message}</p>
        <p className="text-sm text-gray-500 mt-1">
          Contact your administrator to request access.
        </p>
      </div>
    </div>
  );

  return (
    <PermissionGate
      permission={permission}
      requireAll={requireAll}
      fallback={fallback}
      showFallback={true}
    >
      {children}
    </PermissionGate>
  );
};

export default PermissionGate;